import { useState } from "react";
import { gtagReportConversion } from "@/lib/gtag";
import { SEOHead } from "@/components/SEOHead";
import { touristAttractionSchema } from "@/lib/schema";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { WhatsNearby } from "@/components/seo/WhatsNearby";
import { Testimonials } from "@/components/seo/Testimonials";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BookingPopupModal } from "@/components/home/BookingPopupModal";
import { TubingBookingPopupModal } from "@/components/home/TubingBookingPopupModal";
import { 
  Users, 
  Clock, 
  Waves, 
  MapPin, 
  CheckCircle, 
  Sun,
  LifeBuoy,
  Droplets,
  Mountain,
  Sparkles,
  Info,
  Thermometer,
  ShieldCheck,
  CircleDollarSign
} from "lucide-react";

// Images
import tubingHero from "@/assets/tubing-hero.jpg";
import friendsTubing from "@/assets/gallery/friends-tubing.jpg";
import tubingSelfie from "@/assets/gallery/tubing-selfie.jpg";
import tubingCanyonView from "@/assets/gallery/tubing-canyon-view.jpg";
import sonsTubesStack from "@/assets/gallery/sons-tubes-stack.jpg";
import kidWhaleFloat from "@/assets/gallery/kid-whale-float.jpg";
import tubingRiverside from "@/assets/tubing/tubing-riverside.jpg";
import coupleFloating from "@/assets/tubing/couple-floating.jpg";
import sonsTubesRiver from "@/assets/tubing/sons-tubes-river.jpg";
import groupFloating from "@/assets/tubing/group-floating.jpg";
import coupleTube from "@/assets/tubing/couple-tube.jpg";
import familyTubingFun from "@/assets/tubing/family-tubing-fun.jpg";
import guysWithTubes from "@/assets/tubing/guys-with-tubes.jpg";
import friendsFloatingCooler from "@/assets/tubing/friends-floating-cooler.jpg";
import sonsTubesStacked from "@/assets/tubing/sons-tubes-stacked.jpg";
import womanTubingRelaxed from "@/assets/tubing/woman-tubing-relaxed.webp";
import groupTubingFun from "@/assets/tubing/group-tubing-fun.webp";

// Badge images
import aerialProperty from "@/assets/tubing/aerial-property.jpg";
import familyTubes from "@/assets/tubing/family-tubes.jpg";
import friendsRiver from "@/assets/tubing/friends-river.jpg";
import kidTubing from "@/assets/tubing/kid-tubing.jpg";
import groupRiverFloat from "@/assets/tubing/group-river-float.jpg";
import friendsFloatingRiver from "@/assets/tubing/friends-floating-river.webp";
import canyonTubingScene from "@/assets/tubing/canyon-tubing-scene.jpg";
import kidsPlaying from "@/assets/tubing/kids-playing.jpg";

const tubingFeatures = [
  {
    icon: Clock,
    title: "2-3 Hour Float",
    description: "The perfect length to relax without getting bored or exhausted",
    image: friendsFloatingRiver
  },
  {
    icon: Mountain,
    title: "Scenic Canyon Views",
    description: "Drift through stunning limestone canyon walls and lush scenery",
    image: canyonTubingScene
  },
  {
    icon: MapPin,
    title: "End at Your Cabana",
    description: "Float right back to your private basecamp — no shuttle needed",
    image: aerialProperty
  },
  {
    icon: LifeBuoy,
    title: "Quality Tubes Included",
    description: "Heavy-duty river tubes with handles and cup holders",
    image: familyTubes
  },
  {
    icon: Droplets,
    title: "Crystal Clear Water",
    description: "The Guadalupe's spring-fed waters stay refreshingly cool",
    image: friendsRiver
  },
  {
    icon: ShieldCheck,
    title: "Safe & Family-Friendly",
    description: "Calm waters perfect for all ages and skill levels",
    image: kidsPlaying
  }
];

const pricingOptions = [
  {
    title: "Single Tube",
    price: "$29.99",
    unit: "per person",
    description: "Individual river tube with backrest",
    features: ["Heavy-duty construction", "Built-in cup holder", "Comfortable backrest", "2-3 hour float time"]
  },
  {
    title: "Cooler Tube",
    price: "$19.99",
    unit: "per cooler",
    description: "Keep your drinks cold on the river",
    features: ["Fits standard coolers", "Tether rope included", "Keeps refreshments close", "Floats alongside you"]
  },
  {
    title: "Parking",
    price: "$24.99",
    unit: "per car",
    description: "You can book extra parking with your reservation or onsite.",
    features: []
  }
];

const includedWithCabana = [
  { icon: MapPin, text: "Float ends at your cabana" },
  { icon: Waves, text: "Private river access point" },
  { icon: Sun, text: "Shaded rest area waiting" },
  { icon: Thermometer, text: "Cold drinks in your cooler" }
];

const rules = [
  { icon: LifeBuoy, text: "Life jackets available (required for non-swimmers)", allowed: true },
  { icon: Users, text: "Children must be with adults", allowed: true },
  { icon: CircleDollarSign, text: "Cash & card accepted", allowed: true },
  { icon: Sparkles, text: "No glass containers on the river", allowed: false },
  { icon: Info, text: "No outside tubes (rentals required)", allowed: false }
];

const galleryImages = [
  { src: friendsTubing, alt: "Friends tubing on the Guadalupe" },
  { src: tubingSelfie, alt: "Tubing selfie on the river" },
  { src: tubingCanyonView, alt: "Canyon views while tubing" },
  { src: kidWhaleFloat, alt: "Kid on whale float" },
  { src: sonsTubesStack, alt: "Stack of river tubes ready to go" },
  { src: tubingRiverside, alt: "Enjoying the river with tubes" },
  { src: coupleFloating, alt: "Couple relaxing on river tubes" },
  { src: sonsTubesRiver, alt: "Son's Tubes stacked by the river" },
  { src: groupFloating, alt: "Group floating down the river" },
  { src: coupleTube, alt: "Couple sharing a tube on the river" },
  { src: familyTubingFun, alt: "Family having fun tubing" },
  { src: guysWithTubes, alt: "Guys relaxing with river tubes" },
  { src: friendsFloatingCooler, alt: "Friends floating with cooler on the river" },
  { src: sonsTubesStacked, alt: "Son's Tubes stacked by the riverbank" },
  { src: womanTubingRelaxed, alt: "Woman relaxing on a tube" },
  { src: groupTubingFun, alt: "Group having fun tubing together" }
];

const faqs = [
  {
    question: "How long is the tubing float?",
    answer: "The float takes approximately 2-3 hours depending on water levels and how leisurely you take it. It's the perfect length to enjoy the scenery without getting too tired or sunburned."
  },
  {
    question: "Do I need to be a strong swimmer?",
    answer: "No, the river is calm and shallow in most areas and we provide FREE life jackets to use which is required for non or weak swimmers. Children should always be accompanied by adults."
  },
  {
    question: "Can I bring my own tube?",
    answer: "Outside tubes are not permitted on our property. We provide high-quality, heavy-duty tubes for rent to ensure safety and a great experience for everyone."
  },
  {
    question: "What should I bring?",
    answer: "Sunscreen, sunglasses with a strap, water shoes or sandals that stay on, and a waterproof phone case. Skip the glass containers — cans and plastic only on the river."
  },
  {
    question: "What if I don't have a cabana?",
    answer: "That's perfectly fine, you don't have to have a cabana or picnic table reserved to float with us though having one is the beauty of our setup, in that you float right back to your private cabana at the end!"
  },
  {
    question: "Is tubing safe for kids?",
    answer: "Yes, as long as an adult is supervising and kids have life jackets."
  },
  {
    question: "What happens if the weather is bad?",
    answer: "Light rain doesn't stop the fun — you're already wet! For severe weather (lightning, flash flooding), we'll pause operations for safety. Check our cancellation policy for refund details."
  }
];

const Tubing = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [tubingOpen, setTubingOpen] = useState(false);
  
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Tubing the Guadalupe River, New Braunfels | Son's"
        description="Family-friendly tubing the Guadalupe River near New Braunfels. Reserve tubes online and float from your private cabana — no shuttle hassles."
        canonical="/tubing"
        ogImage="/og/tubing.jpg"
        schemaJson={[
          touristAttractionSchema(
            "Tubing the Guadalupe River Near New Braunfels",
            "Family-friendly tubing on the Guadalupe River near New Braunfels, TX with cabana rentals, kayaks, and direct river access.",
          ),
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
              { "@type": "ListItem", position: 2, name: "Tubing", item: "https://sonsguadalupe.com/tubing" },
            ],
          },
        ]}
      />
      <BookingPopupModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <TubingBookingPopupModal open={tubingOpen} onOpenChange={setTubingOpen} />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={tubingHero}
            alt="Family tubing on the crystal clear Guadalupe River"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 via-charcoal/50 to-transparent" />
        </div>
        
        <div className="relative z-10 container-narrow mx-auto px-4 md:px-8 pt-32 pb-16">
          <div className="max-w-2xl">
            <p className="text-coral-orange font-medium mb-4 text-lg">Tubing Experience</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              The Float That Ends at Your Doorstep
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8">
              2-3 hours drifting through limestone canyons on crystal-clear water — then float right back to YOUR private cabana. No shuttles, no crowds, no stress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="hero" size="xl" onClick={() => { gtagReportConversion(); setTubingOpen(true); }}>
                Go Tubing — $29.99/person
              </Button>
              <Button variant="hero-outline" size="xl" onClick={() => { gtagReportConversion(); setBookingOpen(true); }}>
                Book a Cabana First
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Why Our Tubing Experience is Different
            </h2>
            <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
              Forget crowded public access points and sketchy shuttle rides. This is tubing done right.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tubingFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-shadow"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={feature.image}
                    loading="lazy"
                    alt={feature.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center mb-3">
                    <feature.icon className="w-5 h-5 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              THE BEST FLOAT on the Guad!
            </h2>
            <p className="text-charcoal/70 text-lg max-w-3xl mx-auto">
              Come enjoy a beautiful day on the gorgeous Guadalupe River tubing with friends and family! With hundreds of tubes and brand-new shuttle vans we can get you on the river and having fun in no time!
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden rounded-xl ${
                  index === 0 || index === 7 ? 'col-span-2 row-span-2' : ''
                }`}
              >
                <img
                  loading="lazy"
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding bg-teal text-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              No hidden fees. Just grab your tube and go.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {pricingOptions.map((option, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <h3 className="font-heading text-xl font-bold mb-2">{option.title}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-coral-orange">{option.price}</span>
                  <span className="text-white/70 ml-2">{option.unit}</span>
                </div>
                <p className="text-white/80 mb-6">{option.description}</p>
                <ul className="space-y-2">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/90 text-sm">
                      <CheckCircle className="w-4 h-4 text-coral-orange flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center flex flex-col items-center gap-3">
            <Button 
              variant="hero" 
              size="xl" 
              onClick={() => setTubingOpen(true)}
              className="bg-coral-orange hover:bg-coral-orange/90"
            >
              Book Now
            </Button>
            <button 
              onClick={() => setBookingOpen(true)}
              className="bg-coral-orange text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-lg hover:bg-coral-orange/90 transition-colors cursor-pointer"
            >
              🎉 Save! - 25% OFF Tubing When You Rent a Cabana
            </button>
          </div>
        </div>
      </section>

      {/* The Perfect Combo */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={tubingCanyonView}
                alt="Tubing through the canyon"
                className="rounded-2xl shadow-elevated"
                loading="lazy"
              />
            </div>
            
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6">
                The Perfect Combo: Cabana + Tubing
              </h2>
              <p className="text-charcoal/70 text-lg mb-8">
                Here's what makes our tubing experience unique: you float right back to your private cabana. No waiting for shuttles, no hiking back to a parking lot. Just drift back to your shaded home base where your cooler, grill, and comfy chairs are waiting.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {includedWithCabana.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-teal" />
                    </div>
                    <span className="text-charcoal/80">{item.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col items-start gap-3">
                <Button variant="default" size="lg" onClick={() => setBookingOpen(true)}>
                  Book Cabana + Tubing Package
                </Button>
                <button 
                  onClick={() => setBookingOpen(true)}
                  className="bg-coral-orange text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-lg hover:bg-coral-orange/90 transition-colors cursor-pointer"
                >
                  🎉 Save! - 25% OFF Tubing When You Rent a Cabana
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules & Safety */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
                River Rules & Safety
              </h2>
              <p className="text-charcoal/70 text-lg">
                A few guidelines to keep everyone safe and having fun.
              </p>
            </div>
            
            <div className="bg-cream rounded-2xl p-8">
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-center gap-4 py-3 border-b border-border last:border-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      rule.allowed ? 'bg-deep-green/10' : 'bg-coral-orange/10'
                    }`}>
                      {rule.allowed ? (
                        <CheckCircle className="w-5 h-5 text-deep-green" />
                      ) : (
                        <Info className="w-5 h-5 text-coral-orange" />
                      )}
                    </div>
                    <span className="text-charcoal">{rule.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Tubing FAQs
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-white rounded-xl p-6 cursor-pointer shadow-soft"
              >
                <summary className="flex items-center justify-between font-heading font-bold text-charcoal text-lg list-none">
                  {faq.question}
                  <Sun className="w-5 h-5 text-teal group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-4 text-charcoal/70 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-deep-green text-white">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Float the Guadalupe?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Book your cabana and add tubing for the ultimate river day experience. No crowds, no shuttles — just pure relaxation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="xl" onClick={() => setBookingOpen(true)}>
              Book Your River Day
            </Button>
            <Button variant="hero-outline" size="xl" className="border-white text-white hover:bg-white/10">
              Call 877-577-7667
            </Button>
          </div>
        </div>
      </section>

      <WhatsNearby />

      <Testimonials category="tubing" />
      <SEOContentBlock {...seoCopy.tubing} />
      <Footer />
    </div>
  );
};

export default Tubing;
